import { useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { EffectFade, Parallax } from "swiper/modules";
import Footer from "./Footer";
import "swiper/css";
import "swiper/css/effect-fade";

const MOBILE_MQ = "(max-width: 767px)";
const REDUCED_MOTION_MQ = "(prefers-reduced-motion: reduce)";
const DESKTOP_SPEED_MS = 900;
const WHEEL_THRESHOLD = 30;
const SWIPE_MIN_DISTANCE = 40;
const SWIPE_MAX_TIME_MS = 400;
const SWIPE_MIN_VELOCITY = 0.35;

const services = [
  {
    num: "01",
    short: "Web",
    name: "Web Design",
    tag: "Digital Presence",
    desc: "Beautiful, responsive websites crafted to capture your brand and convert visitors into loyal clients.",
    image: "/images/web_design.jpg",
  },
  {
    num: "02",
    short: "UX",
    name: "UI/UX Design",
    tag: "Experience",
    desc: "Intuitive, user-centered interfaces that guide users effortlessly and keep them coming back.",
    image: "/images/UIUX_Design.jpg",
  },
  {
    num: "03",
    short: "Stack",
    name: "Full-Stack Apps",
    tag: "Development",
    desc: "Robust, scalable web applications built end-to-end — from database to deployment.",
    image: "/images/Full-Stack Apps.jpg",
  },
  {
    num: "04",
    short: "Apps",
    name: "iOS & Android Apps",
    tag: "Mobile",
    desc: "Native and cross-platform mobile experiences that go wherever your users go.",
    image: "/images/Mobile_Apps.jpg",
  },
  {
    num: "05",
    short: "IT",
    name: "IT Services",
    tag: "Infrastructure",
    desc: "Reliable hardware support, network management, and technical solutions for your business.",
    image: "/images/IT_support.jpg",
  },
];

const navBtnFocus =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E4080A] focus-visible:ring-offset-2 focus-visible:ring-offset-gray-900";

function pad(n) {
  return String(n).padStart(2, "0");
}

function subscribeMediaQuery(query, onMatch) {
  const mq = window.matchMedia(query);
  const handler = () => onMatch(mq.matches);

  handler();
  if (mq.addEventListener) {
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }
  mq.addListener(handler);
  return () => mq.removeListener(handler);
}

function preloadImage(src) {
  const img = new Image();
  img.src = src;
}

function ServiceSlide({ service, compact = false, eager = false, useParallax = true }) {
  return (
    <div className={compact ? "relative min-h-full shrink-0" : "relative h-full"}>
      <div
        className="srv-slide-bg absolute inset-0 overflow-hidden bg-gray-900"
        {...(useParallax ? { "data-swiper-parallax-y": "28%" } : {})}
      >
        <img
          src={service.image}
          alt=""
          loading="eager"
          decoding="async"
          fetchPriority={eager ? "high" : "low"}
          className="srv-slide-img absolute inset-0 h-full w-full object-cover"
          aria-hidden="true"
        />
      </div>

      <div className="srv-slide-overlays" aria-hidden="true" />

      <div
        className={
          compact
            ? "srv-slide-copy relative z-10 flex min-h-full flex-col justify-center px-8 md:min-h-0 md:h-full md:px-16 md:pb-4 md:pt-16"
            : "srv-slide-copy relative z-10 flex h-full flex-col justify-center px-8 md:px-16 md:pb-20 md:pt-40"
        }
      >
        <span
          className="srv-slide-tag mb-2 font-mono text-sm uppercase tracking-widest text-[#ff4d4f] md:mb-3 md:text-[#E4080A]"
          {...(useParallax ? { "data-swiper-parallax-y": "-18%" } : {})}
        >
          {service.tag} &mdash; {service.num}
        </span>
        <h2
          className={
            compact
              ? "srv-slide-title mb-3 text-4xl font-bold leading-none text-white md:mb-4 md:text-5xl lg:text-6xl"
              : "srv-slide-title mb-4 text-[2.65rem] font-bold leading-[0.95] text-white md:mb-6 md:text-7xl md:leading-none lg:text-[7rem]"
          }
          {...(useParallax ? { "data-swiper-parallax-y": "-32%" } : {})}
        >
          {service.name}
        </h2>
        <p
          className="srv-slide-desc max-w-xl text-base leading-relaxed text-neutral-100 md:text-lg md:text-neutral-300"
          {...(useParallax ? { "data-swiper-parallax-y": "-14%", "data-swiper-parallax-opacity": "0.5" } : {})}
        >
          {service.desc}
        </p>
      </div>
    </div>
  );
}

function TopLabel({ isLast }) {
  return (
    <div className={`absolute inset-x-0 top-0 z-20 px-8 py-3 md:top-16 md:px-16 md:py-5 ${isLast ? "hidden md:block" : ""}`}>
      <span className="pointer-events-none font-mono text-xs uppercase tracking-[0.3em] text-[#E4080A]">
        What We Do
      </span>
    </div>
  );
}

function MobileNav({ activeIndex, activeService, onSelect }) {
  return (
    <nav aria-label="Slide progress" className="srv-mobile-nav md:hidden">
      <div className="srv-mobile-nav-inner">
        <div className="srv-mobile-nav-row mb-3">
          <span className="shrink-0 rounded-lg border border-[#E4080A]/45 bg-[#E4080A]/10 px-2 py-1 font-mono text-[11px] font-semibold tracking-wider text-[#E4080A]">
            {activeService.num}
          </span>
          <div className="min-w-0 flex-1">
            <span className="block truncate text-[13px] font-semibold text-white">
              {activeService.name}
            </span>
            <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-widest text-neutral-400">
              {activeService.tag}
            </span>
          </div>
          <span className="shrink-0 font-mono text-xs tabular-nums text-white">
            {pad(activeIndex + 1)}
            <span className="text-neutral-500">/</span>
            {pad(services.length)}
          </span>
        </div>

        <div className="srv-mobile-nav-row flex gap-1.5">
          {services.map((service, i) => (
            <button
              key={service.num}
              type="button"
              aria-label={service.name}
              aria-current={i === activeIndex ? "true" : undefined}
              onClick={() => onSelect(i)}
              className={`flex flex-1 border-0 bg-transparent py-1.5 ${navBtnFocus}`}
            >
              <span
                className={`block w-full rounded-full transition-all duration-300 ${i === activeIndex
                    ? "h-1.5 bg-[#E4080A] shadow-[0_0_14px_rgba(228,8,10,0.65)]"
                    : i < activeIndex
                      ? "h-1 bg-neutral-500"
                      : "h-1 bg-neutral-700"
                  }`}
              />
            </button>
          ))}
        </div>
      </div>
    </nav>
  );
}

function DesktopNav({ activeIndex, activeService, onSelect }) {
  return (
    <nav aria-label="Services" className="absolute inset-x-0 bottom-0 z-20 hidden px-16 pb-9 pt-10 md:block">
      <div className="mb-4 flex items-end justify-between gap-4">
        <p className="font-mono text-xs leading-relaxed tracking-wide">
          <span className="text-[#E4080A]">{activeService.num}</span>
          <span className="text-neutral-600"> — </span>
          <span className="text-white">{activeService.name}</span>
        </p>
        <span className="shrink-0 font-mono text-[10px] tabular-nums tracking-widest text-neutral-600">
          {activeService.num}/{pad(services.length)}
        </span>
      </div>

      <div className="flex gap-2">
        {services.map((service, i) => (
          <button
            key={service.num}
            type="button"
            aria-label={service.name}
            aria-current={i === activeIndex ? "true" : undefined}
            onClick={() => onSelect(i)}
            className={`group flex flex-1 flex-col gap-2 border-0 bg-transparent p-0 ${navBtnFocus}`}
          >
            <span
              className={`block h-[3px] rounded-full transition-all duration-500 ${i === activeIndex
                  ? "bg-[#E4080A]"
                  : i < activeIndex
                    ? "bg-neutral-500"
                    : "bg-neutral-800 group-hover:bg-neutral-600"
                }`}
            />
            <span
              className={`font-mono text-[9px] uppercase tracking-widest transition-colors ${i === activeIndex ? "text-[#E4080A]" : "text-neutral-600 group-hover:text-neutral-400"
                }`}
            >
              {service.short}
            </span>
          </button>
        ))}
      </div>
    </nav>
  );
}

export default function StickyServicesReveal() {
  const viewportRef = useRef(null);
  const lastSlideRef = useRef(null);
  const swiperRef = useRef(null);
  const activeIndexRef = useRef(0);
  const wheelLockedRef = useRef(false);
  const wheelInViewRef = useRef(true);

  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia(MOBILE_MQ).matches : false
  );
  const [reducedMotion, setReducedMotion] = useState(false);

  const lastIndex = services.length - 1;
  const activeService = services[activeIndex];
  const useParallax = !isMobile && !reducedMotion;

  const goToSlide = (index) => swiperRef.current?.slideTo(index);

  useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  useEffect(() => {
    return subscribeMediaQuery(MOBILE_MQ, setIsMobile);
  }, []);

  useEffect(() => {
    return subscribeMediaQuery(REDUCED_MOTION_MQ, setReducedMotion);
  }, []);

  // Kick off all slide images immediately — loading="eager" handles it but
  // this also warms the cache for browsers that deprioritise off-screen eager images
  useEffect(() => {
    services.forEach((s) => preloadImage(s.image));
  }, []);

  // Only hijack wheel when carousel is visible in viewport
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        wheelInViewRef.current = entry.isIntersecting && entry.intersectionRatio > 0.25;
      },
      { threshold: [0, 0.25, 0.5, 1] }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Swipe down at top of last slide → previous slide for mobile mode
  useEffect(() => {
    const el = lastSlideRef.current;
    if (!el || activeIndex !== lastIndex) return;

    let startX = 0;
    let startY = 0;
    let startTime = 0;
    let startScrollTop = 0;

    const onTouchStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startTime = Date.now();
      startScrollTop = el.scrollTop;
    };

    const onTouchEnd = (e) => {
      if (activeIndexRef.current !== lastIndex) return;
      if (startScrollTop > 0 || el.scrollTop > 0) return;

      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      const dt = Date.now() - startTime;

      if (dy < SWIPE_MIN_DISTANCE) return;
      if (Math.abs(dx) > Math.abs(dy) * 0.75) return;
      if (dt > SWIPE_MAX_TIME_MS) return;
      if (dy / dt < SWIPE_MIN_VELOCITY) return;

      swiperRef.current?.slidePrev();
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [activeIndex, lastIndex]);

  // custome wheel scroll to make it or smoother than default one 
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || isMobile) return;

    let accumulated = 0;
    let resetTimer;
    let rafId = 0;

    const tryAdvance = () => {
      rafId = 0;
      if (wheelLockedRef.current) {
        accumulated = 0;
        return;
      }
      if (Math.abs(accumulated) < WHEEL_THRESHOLD) return;

      const sw = swiperRef.current;
      if (!sw) return;

      const goingDown = accumulated > 0;
      accumulated = 0;

      if (goingDown && sw.isEnd) return;
      if (!goingDown && sw.isBeginning) return;

      wheelLockedRef.current = true;
      if (goingDown) sw.slideNext();
      else sw.slidePrev();
    };

    const onWheel = (e) => {
      if (!wheelInViewRef.current) return;

      // On the last slide, let the browser scroll the embedded footer into/out
      // of view; only hijack the wheel once it's scrolled to the very top or bottom.
      const sw = swiperRef.current;
      const lastEl = lastSlideRef.current;
      if (sw && sw.isEnd && lastEl && !wheelLockedRef.current) {
        const goingDown = e.deltaY > 0;
        const atTop = lastEl.scrollTop <= 0;
        const atBottom =
          lastEl.scrollTop + lastEl.clientHeight >= lastEl.scrollHeight - 2;
        if ((goingDown && !atBottom) || (!goingDown && !atTop)) return;
      }

      e.preventDefault();
      if (wheelLockedRef.current) return;

      accumulated += e.deltaY;
      clearTimeout(resetTimer);
      resetTimer = setTimeout(() => {
        accumulated = 0;
      }, 150);

      if (!rafId) rafId = requestAnimationFrame(tryAdvance);
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      viewport.removeEventListener("wheel", onWheel);
      clearTimeout(resetTimer);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [isMobile]);

  // Keyboard navigation
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const onKeyDown = (e) => {
      const sw = swiperRef.current;
      if (!sw) return;

      switch (e.key) {
        case "ArrowDown":
        case "PageDown":
          e.preventDefault();
          sw.slideNext();
          break;
        case "ArrowUp":
        case "PageUp":
          e.preventDefault();
          sw.slidePrev();
          break;
        case "Home":
          e.preventDefault();
          sw.slideTo(0);
          break;
        case "End":
          e.preventDefault();
          sw.slideTo(lastIndex);
          break;
        default:
          break;
      }
    };

    viewport.addEventListener("keydown", onKeyDown);
    return () => viewport.removeEventListener("keydown", onKeyDown);
  }, [lastIndex]);

  const onSwiper = (instance) => {
    swiperRef.current = instance;
  };

  const onSlideChange = (instance) => {
    setActiveIndex(instance.activeIndex);
  };

  const onSlideChangeTransitionEnd = () => {
    wheelLockedRef.current = false;
    lastSlideRef.current?.scrollTo({ top: 0, behavior: "instant" });
  };

  return (
    <div
      ref={viewportRef}
      tabIndex={0}
      aria-roledescription="carousel"
      aria-label="Services"
      className="srv-viewport relative w-full overflow-hidden bg-gray-900 select-none focus:outline-none"
    >
      <p className="sr-only" aria-live="polite" aria-atomic="true">
        {activeService.name} — {activeService.tag}
      </p>

      <TopLabel isLast={activeIndex === lastIndex} />

      <Swiper
        className="srv-swiper h-full w-full"
        direction="vertical"
        effect="fade"
        fadeEffect={{ crossFade: true }}
        parallax={useParallax}
        watchSlidesProgress
        slidesPerView={1}
        speed={isMobile ? 650 : DESKTOP_SPEED_MS}
        threshold={5}
        touchRatio={1.2}
        longSwipesRatio={0.35}
        shortSwipes
        touchAngle={45}
        followFinger
        preventInteractionOnTransition={false}
        resistanceRatio={0}
        modules={[EffectFade, Parallax]}
        onSwiper={onSwiper}
        onSlideChange={onSlideChange}
        onSlideChangeTransitionEnd={onSlideChangeTransitionEnd}
      >
        {services.map((service, i) => (
          <SwiperSlide key={service.num} aria-label={`${service.name}, slide ${i + 1} of ${services.length}`}>
            {i === lastIndex ? (
              <div
                ref={lastSlideRef}
                className="srv-last-slide swiper-no-swiping flex h-full flex-col overflow-y-auto overscroll-y-contain touch-pan-y"
              >
                <ServiceSlide service={service} compact useParallax={useParallax} />
                {/* Empty space after the last section (follows lastIndex, so it stays
                    after whatever the final service is if you add/remove sections).
                    The image above fills the screen, so the footer + this gap sit
                    below the fold and are revealed by scrolling. */}
                <div aria-hidden="true" className="h-24 shrink-0 bg-gray-900 md:h-40" />
                <div className="relative z-10 shrink-0 select-text bg-white dark:bg-gray-900 md:pb-32">
                  <Footer embedded />
                </div>
              </div>
            ) : (
              <ServiceSlide service={service} eager={i === 0} useParallax={useParallax} />
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      <MobileNav activeIndex={activeIndex} activeService={activeService} onSelect={goToSlide} />
      <DesktopNav activeIndex={activeIndex} activeService={activeService} onSelect={goToSlide} />
    </div>
  );
}
